import React, { useState, useEffect } from 'react';
import {
    Box, Button, Stack, Paper, FormControl, OutlinedInput,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Chip,
    Switch, Divider, FormGroup, FormControlLabel
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from "react-hot-toast";
import DataGridComponent from '../../components/DataGridComponent';
import { useDispatch, useSelector } from 'react-redux';
import { getBadgesListApi } from '../../store/crowns-and-badges/getbadgesList';
import { updateBadgesApi, resetUpdateBadges } from '../../store/crowns-and-badges/updateBadges';

export default function Badges() {
    const dispatch = useDispatch();
    const { loading, result } = useSelector((state) => state.getBadgesList);
    const { loading: updateLoading, success } = useSelector((state) => state.updateBadges);
    const [badges, setBadges] = useState([]);

    // Modal state
    const [open, setOpen] = useState(false);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [tempValue, setTempValue] = useState('');

    // Confirmation Modal state
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (result && result.length > 0) {
            setBadges(result);
        }
    }, [result]);

    useEffect(() => {
        if (success) {
            dispatch(resetUpdateBadges());
            dispatch(getBadgesListApi());
            handleModalClose();
            handleConfirmClose();
        }
    }, [success, dispatch]);

    const handleEditClick = (badge) => {
        const isContributor = badge.badge_code === 'contributor';
        const value = isContributor ? badge.min_contributions : badge.threshold_value;
        setSelectedBadge(badge);
        // Convert minutes to hours for display in modal, except for contributor
        const hoursValue = (value && !isContributor) ? (value / 60) : (value || '');
        setTempValue(hoursValue);
        setOpen(true);
    };

    const handleModalClose = () => {
        setOpen(false);
        setSelectedBadge(null);
        setTempValue('');
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
        setSelectedBadge(null);
    };

    const handleStatusClick = (badge) => {
        setSelectedBadge(badge);
        setConfirmOpen(true);
    };

    const handleModuleToggle = (moduleKey) => {
        const isContributor = selectedBadge.badge_code === 'contributor';
        const moduleTypeKey = isContributor ? 'contribution_modules' : 'learner_modules';

        if (selectedBadge[moduleTypeKey]) {
            const updatedModules = selectedBadge[moduleTypeKey].map(mod =>
                mod.key === moduleKey ? { ...mod, is_enabled: !mod.is_enabled } : mod
            );

            setSelectedBadge({
                ...selectedBadge,
                [moduleTypeKey]: updatedModules
            });
        }
    };

    const handleModalSave = async () => {
        if (selectedBadge && !updateLoading) {
            if (!tempValue || parseFloat(tempValue) <= 0) {
                toast.error("Please enter a value greater than 0");
                return;
            }
            const isContributor = selectedBadge.badge_code === 'contributor';
            // Convert hours back to minutes for API, except for contributor
            const updatedValue = isContributor ? (parseFloat(tempValue) || 0) : (Math.round(parseFloat(tempValue) * 60) || 0);

            const updatedBadge = {
                ...selectedBadge,
                [isContributor ? 'min_contributions' : 'threshold_value']: updatedValue
            };

            // Format contribution_types or learner_enabled_modules based on toggled states
            if (isContributor && selectedBadge.contribution_modules) {
                updatedBadge.contribution_types = selectedBadge.contribution_modules
                    .filter(mod => mod.is_enabled)
                    .map(mod => mod.key);
            } else if (!isContributor && selectedBadge.learner_modules) {
                updatedBadge.learner_enabled_modules = selectedBadge.learner_modules
                    .filter(mod => mod.is_enabled)
                    .map(mod => mod.key);
            }

            const payload = updatedBadge;

            try {
                await dispatch(updateBadgesApi(payload)).unwrap();
                // Success handled in useEffect
            } catch (err) {
                dispatch(resetUpdateBadges());
            }
        }
    };

    const handleConfirmSave = async () => {
        if (selectedBadge && !updateLoading) {
            const payload = {
                ...selectedBadge,
                is_enabled: !selectedBadge.is_enabled
            };

            try {
                await dispatch(updateBadgesApi(payload)).unwrap();
                // Success handled in useEffect
            } catch (err) {
                dispatch(resetUpdateBadges());
            }
        }
    };

    const columns = [
        {
            field: 'badge_name',
            headerName: 'Badge Name',
            flex: 1,
            sortable: false,
            filterable: false,
        },
        {
            field: 'threshold_value',
            headerName: 'Threshold Value',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isContributor = params.row.badge_code === 'contributor';
                const value = isContributor ? params.row.min_contributions : params.row.threshold_value;
                // Convert minutes to hours for display, except for contributor
                if (isContributor) return value || 0;
                return value ? (value / 60).toFixed(1).replace(/\.0$/, '') : 0;
            },
        },
        {
            field: 'is_enabled',
            headerName: 'Status',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Chip
                    color={params.row.is_enabled ? "success" : "error"}
                    label={params.row.is_enabled ? "Active" : "InActive"}
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row)}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    color="info"
                    endIcon={<EditIcon />}
                    label="Available"
                    variant="outlined"
                    sx={{ margin: "0px 10px" }}
                    onClick={() => handleEditClick(params.row)}
                >
                    EDIT
                </Button>
            ),
        }
    ];

    useEffect(() => {
        dispatch(getBadgesListApi());
    }, [dispatch]);

    return (
        <Box>
            <Paper elevation={1} sx={{ mt: 2 }}>
                <DataGridComponent
                    rows={badges}
                    columns={columns}
                    getRowId={(row) => row.badge_code}
                    pagination={false}
                    paginationMode="client"
                    filterMode="client"
                    sortingMode="client"
                    hideFooter
                    disableColumnFilter
                    disableColumnMenu
                    autoHeight
                    loading={loading}
                    sx={{
                        height: 'auto',
                        '& .MuiDataGrid-virtualScrollerContent': { minHeight: 'unset !important' },
                        '& .MuiDataGrid-cell': { py: '12px' },
                    }}
                    slots={{ toolbar: () => null }}
                />
            </Paper>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                {/* <Button variant="contained" onClick={handleSave} disabled={loading}>
                    Save
                </Button> */}
            </Stack>

            {/* Edit Modal */}
            <Dialog open={open} onClose={handleModalClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Edit Badge: {selectedBadge?.badge_name}</Typography>
                        <IconButton onClick={handleModalClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ py: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {selectedBadge?.badge_code === 'contributor' ? 'Min Contributions' : 'Threshold Value (Hours)'}
                        </Typography>
                        <FormControl variant="outlined" fullWidth>
                            <OutlinedInput
                                type="text"
                                value={tempValue}
                                onChange={(e) => {
                                    setTempValue(e.target.value.replace(/[^0-9]/g, ''))
                                }}
                                placeholder="0"
                            />
                        </FormControl>
                    </Box>

                    {(selectedBadge?.contribution_modules || selectedBadge?.learner_modules) && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                                Available Modules
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(selectedBadge?.contribution_modules || selectedBadge?.learner_modules || []).map((module) => (
                                    <Box
                                        key={module.key}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            py: 0.5,
                                            px: 1,
                                            borderRadius: 1
                                        }}
                                    >
                                        <Typography variant="body2">{module.label}</Typography>
                                        <Switch
                                            size="small"
                                            checked={module.is_enabled || false}
                                            onChange={() => handleModuleToggle(module.key)}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleModalClose} disabled={updateLoading}>Cancel</Button>
                    <Button variant="contained" onClick={handleModalSave} disabled={updateLoading}>
                        {updateLoading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Status Confirmation Modal */}
            <Dialog open={confirmOpen} onClose={handleConfirmClose} fullWidth maxWidth="xs">
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton onClick={handleConfirmClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {`Are you sure you want to change the status of ${selectedBadge?.badge_name} badge?`}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                            Please click confirm
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleConfirmClose}
                        sx={{ px: 4, borderRadius: 1, borderColor: '#00adef', color: '#00adef' }}
                        disabled={updateLoading}
                    >
                        BACK
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmSave}
                        sx={{ px: 4, borderRadius: 1, bgcolor: '#00adef', '&:hover': { bgcolor: '#009ad4' } }}
                        disabled={updateLoading}
                    >
                        {updateLoading ? 'SAVING...' : 'CONFIRM'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
